import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { SSOService } from './sso.service';
import { environment } from '../../environments/environment';

export interface SignalRNotification {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
}

export interface BackupProgressEvent {
  projectId: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  filesProcessed: number;
  totalFiles: number;
}

export interface TestReportSyncProgressEvent {
  projectId: number;
  reportType: 'inverter' | 'transformer';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  filesProcessed: number;
  totalFiles: number;
  skipped: number;
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection?: signalR.HubConnection;
  private notificationSubject = new BehaviorSubject<SignalRNotification | null>(null);
  private backupProgressSubject = new BehaviorSubject<BackupProgressEvent | null>(null);
  private testReportSyncProgressSubject = new BehaviorSubject<TestReportSyncProgressEvent | null>(null);
  private connectionStateSubject = new BehaviorSubject<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );

  public notification$ = this.notificationSubject.asObservable();
  public backupProgress$ = this.backupProgressSubject.asObservable();
  public testReportSyncProgress$ = this.testReportSyncProgressSubject.asObservable();
  public connectionState$ = this.connectionStateSubject.asObservable();

  private ssoService = inject(SSOService);

  async startConnection(): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    // Get JWT token from existing auth
    const token = await this.getAccessToken();
    if (!token) {
      console.error('SignalR: No access token available');
      return;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiEndPoint}notificationHub`, {
        accessTokenFactory: () => token,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 10s, 30s, then 30s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          return 30000;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Register event handlers
    this.registerHandlers();

    try {
      await this.hubConnection.start();
      console.log('SignalR connected successfully');
      this.connectionStateSubject.next(this.hubConnection.state);
    } catch (err: any) {
      console.error('SignalR connection error:', err);
      this.connectionStateSubject.next(signalR.HubConnectionState.Disconnected);
    }
  }

  async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      await this.hubConnection.stop();
      console.log('SignalR connection stopped');
      this.connectionStateSubject.next(signalR.HubConnectionState.Disconnected);
    }
  }

  private registerHandlers(): void {
    if (!this.hubConnection) return;

    // Listen for notifications from backend
    this.hubConnection.on('ReceiveNotification', (notification: SignalRNotification) => {
      console.log('Received notification:', notification);
      this.notificationSubject.next(notification);
    });

    // Listen for backup progress events
    this.hubConnection.on('BackupProgress', (event: BackupProgressEvent) => {
      this.backupProgressSubject.next(event);
    });

    // Listen for test report sync progress events
    this.hubConnection.on('TestReportSyncProgress', (event: TestReportSyncProgressEvent) => {
      this.testReportSyncProgressSubject.next(event);
    });

    // Connection state handlers
    this.hubConnection.onreconnecting(() => {
      console.log('SignalR reconnecting...');
      this.connectionStateSubject.next(signalR.HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected(() => {
      console.log('SignalR reconnected');
      this.connectionStateSubject.next(signalR.HubConnectionState.Connected);
    });

    this.hubConnection.onclose(() => {
      console.log('SignalR connection closed');
      this.connectionStateSubject.next(signalR.HubConnectionState.Disconnected);
    });
  }

  private async getAccessToken(): Promise<string> {
    // Get token from existing MSAL authentication via SSOService
    try {
      const token = await this.ssoService.getAccessToken();
      if (!token) {
        console.warn('No access token available from SSOService');
        return '';
      }
      return token;
    } catch (error) {
      console.error('Failed to acquire token for SignalR:', error);
      return '';
    }
  }
}
